import React, { forwardRef, useMemo } from 'react';
import CurrencyFormat from 'utils/currencyFormat';
import { MonthYearFormat } from 'utils/dateFormat';
import { ReactComponent as EarnipayLogoWhite } from "assets/images/icons/project-icons/earnipay-white.svg";
import useLoginInfo from 'hooks/useLoginInfo';

export const PaySlip = forwardRef(({ transaction, payroll }, ref) => {
    const { userInfo } = useLoginInfo();
    
    const totalEarnings = useMemo(() => {
        return transaction?.net_pay + transaction?.manual_bonuses;
    }, [transaction]);
    
    const totalDeductions = useMemo(() => {
        return transaction?.pension_deduction?.employee_amount + transaction?.manual_deductions + transaction?.on_demand_pay;
    }, [transaction]);
    
    return (
        <div ref={ref} className='h-screen flex flex-col justify-between payslip-wrapper'>
            <div className="p-6">
                <div className='text-xl font-semibold'>Payslip</div>
                <div className='mt-6'>
                    <div className='w-20 h-20'>
                        <img 
                            src={userInfo?.company?.businessLogo} 
                            alt={`${userInfo?.company?.businessName || "-"}'s logo`} 
                            className='w-full h-full'
                        />
                    </div>
                    <p className="text-lg font-medium mt-2 text-[#464646]">
                        {userInfo?.company?.businessName || "-"}
                    </p>
                    <p className="text-base text-[#6B6B6B]">
                        {userInfo?.company?.address || "-"}
                    </p>
                </div>
                <div className="mt-4">
                    <div className="border-dashed border-[#718096] border-t border-b px-3 py-6">
                        <div className="flex items-center gap-6 w-1/2 mb-6">
                            <p className="w-1/2 text-[#707070] text-sm">Employee Name</p>
                            <p className="capitalize w-1/2 text-[#121212] text-sm font-medium">{transaction?.employee_name?.toLowerCase() || "-"}</p>
                        </div>
                        <div className="flex items-center gap-6 w-1/2">
                            <p className="w-1/2 text-[#707070] text-sm">Pay Period</p>
                            <p className="capitalize w-1/2 text-[#121212] text-sm font-medium">{MonthYearFormat(payroll?.payroll_date_range?.end_date)}</p>
                        </div>
                    </div>
                    <div className="border-dashed border-[#718096] border-b flex gap-6 w-full px-3">
                        <div className="w-1/2 pt-6">
                            <p className="text-base font-semibold">Earnings</p>
                            <div className="payslip-amount-breakdown">
                                <div className="flex items-center justify-between gap-6 w-full mt-4">
                                    <p className="w-1/2 text-[#707070] text-sm">Net Pay</p>
                                    <p className="capitalize w-1/2 text-[#121212] text-sm font-medium flex justify-end">{CurrencyFormat(transaction?.net_pay)}</p>
                                </div>
                                <div className="flex items-center justify-between gap-6 w-full mt-4">
                                    <p className="w-1/2 text-[#707070] text-sm">Bonuses</p>
                                    <p className="capitalize w-1/2 text-[#121212] text-sm font-medium flex justify-end">{CurrencyFormat(transaction?.manual_bonuses || "-")}</p>
                                </div>
                            </div>
                            <div className="flex items-center justify-between gap-6 w-full mt-6">
                                <p className="w-1/2 text-[#00535C] text-sm font-semibold">Total Earnings</p>
                                <p className="capitalize w-1/2 text-[#00535C] text-sm font-medium flex justify-end">{CurrencyFormat((totalEarnings) || "-")}</p>
                            </div>
                        </div>
                        <hr className="payslip-separator-hr" />
                        <div className="w-1/2 pt-6">
                            <p className="text-base font-semibold">Deductions</p>
                            <div className="payslip-amount-breakdown">
                                <div className="flex items-center justify-between gap-6 w-full mt-4">
                                    <p className="w-1/2 text-[#707070] text-sm">On-Demand Pay</p>
                                    <p className="capitalize w-1/2 text-[#121212] text-sm font-medium flex justify-end">{CurrencyFormat(transaction?.on_demand_pay || "-")}</p>
                                </div>                          
                                <div className="flex items-center justify-between gap-6 w-full mt-4">
                                    <p className="w-1/2 text-[#707070] text-sm">Pension Deduction</p>
                                    <p className="capitalize w-1/2 text-[#121212] text-sm font-medium flex justify-end">{CurrencyFormat(transaction?.pension_deduction?.employee_amount || "-")}</p>
                                </div>                         
                                <div className="flex items-center justify-between gap-6 w-full mt-4">
                                    <p className="w-1/2 text-[#707070] text-sm">Other Deductions</p>
                                    <p className="capitalize w-1/2 text-[#121212] text-sm font-medium flex justify-end">{CurrencyFormat(transaction?.manual_deductions || "-")}</p>
                                </div>                       
                            </div>
                            <div className="flex items-center justify-between gap-6 w-full mt-6">
                                <p className="w-1/2 text-[#00535C] text-sm font-semibold">Total Deductions</p>
                                <p className="capitalize w-1/2 text-[#00535C] text-sm font-medium flex justify-end">{CurrencyFormat((totalDeductions) || "-")}</p>
                            </div>   
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <div className="flex items-center justify-end gap-6 w-2/5 mt-4">
                            <p className="w-1/2 text-[#0A0D14] text-base font-semibold">Actual Pay</p>
                            <p className="capitalize w-1/2 text-[#0A0D14] text-base font-semibold flex justify-end">{CurrencyFormat((totalEarnings - totalDeductions) || "-")}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="payslip-footer">
                <div className='flex items-center justify-center h-full'>
                    <p className='text-ep-white text-sm'>Generated by</p>
                    &nbsp;&nbsp;
                    <EarnipayLogoWhite className='w-24 h-16' />
                </div>
            </div>
        </div>
    );
});
